// ReactionBar — Teams/Slack-style emoji reaction pills for a post or comment.
//
// Renders one pill per emoji already in use (emoji + count), highlighted when
// the current user is among the reactors, plus a trailing "add reaction"
// button that opens a full-Unicode emoji picker (emoji-picker-react). Picking
// or clicking a pill toggles that single emoji — a user may hold several
// *different* emoji on the same target at once, unlike the single toggleable
// upvote (VoteControl).
//
// Usage:
//   <ReactionBar target="post"    targetId="abc-123" initialReactions={post.reactions} />
//   <ReactionBar target="comment" targetId="42"      initialReactions={c.reactions} />

import { useCallback, useEffect, useRef, useState } from "react";
import EmojiPicker, { Theme, type EmojiClickData } from "emoji-picker-react";
import {
  toggleReaction,
  type ReactionSummary,
  type ReactionTarget,
} from "./reactions-api";

// ---------------------------------------------------------------------------
// Dark-mode detection — mirrors the [data-theme="dark"] toggle on <html>
// applied by hub-feed/ThemeToggle.tsx, so the picker's own theme follows the
// app's manual light/dark switch rather than only the OS preference.
// ---------------------------------------------------------------------------

function useIsDarkMode(): boolean {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark",
  );

  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(target.getAttribute("data-theme") === "dark");
    });
    observer.observe(target, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ReactionBarProps {
  /** Whether this bar reacts to a post or a comment. */
  target: ReactionTarget;
  /** The id of the post (string) or comment (number serialised to string). */
  targetId: string;
  /** Initial grouped reaction summary — used as display until the first toggle. */
  initialReactions: ReactionSummary[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ReactionBar({ target, targetId, initialReactions }: ReactionBarProps) {
  const [reactions, setReactions] = useState<ReactionSummary[]>(initialReactions);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyEmoji, setBusyEmoji] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isDark = useIsDarkMode();

  useEffect(() => {
    setReactions(initialReactions);
  }, [initialReactions]);

  // Close the picker popover on outside click or Escape.
  useEffect(() => {
    if (!pickerOpen) return;
    function handlePointerDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setPickerOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pickerOpen]);

  const applyToggle = useCallback(
    async (emoji: string) => {
      if (busyEmoji) return;

      // Optimistic update: flip this emoji's pill locally.
      const prev = reactions;
      const existing = prev.find((r) => r.emoji === emoji);
      const optimistic = existing
        ? existing.reacted_by_me
          ? prev
              .map((r) =>
                r.emoji === emoji ? { ...r, count: r.count - 1, reacted_by_me: false } : r,
              )
              .filter((r) => r.count > 0)
          : prev.map((r) =>
              r.emoji === emoji ? { ...r, count: r.count + 1, reacted_by_me: true } : r,
            )
        : [...prev, { emoji, count: 1, reacted_by_me: true }];

      setReactions(optimistic);
      setBusyEmoji(emoji);
      try {
        const result = await toggleReaction(target, targetId, emoji);
        setReactions(result.reactions);
      } catch {
        setReactions(prev);
      } finally {
        setBusyEmoji(null);
      }
    },
    [busyEmoji, reactions, target, targetId],
  );

  const handlePillClick = useCallback(
    (emoji: string) => {
      void applyToggle(emoji);
    },
    [applyToggle],
  );

  const handleEmojiPick = useCallback(
    (data: EmojiClickData) => {
      setPickerOpen(false);
      void applyToggle(data.emoji);
    },
    [applyToggle],
  );

  return (
    <div className="reaction-bar" data-testid="reaction-bar" ref={wrapRef}>
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          className={"reaction-pill" + (r.reacted_by_me ? " on" : "")}
          onClick={() => handlePillClick(r.emoji)}
          disabled={busyEmoji === r.emoji}
          aria-pressed={r.reacted_by_me}
          aria-label={`${r.emoji} reaction, ${r.count} ${r.count === 1 ? "person" : "people"}${
            r.reacted_by_me ? " including you" : ""
          } — click to toggle`}
          data-testid="reaction-pill"
        >
          <span aria-hidden="true">{r.emoji}</span>
          <span className="reaction-pill__count">{r.count}</span>
        </button>
      ))}

      <button
        type="button"
        className="reaction-add-btn"
        onClick={() => setPickerOpen((v) => !v)}
        aria-label="Add a reaction"
        aria-expanded={pickerOpen}
        data-testid="reaction-add-btn"
      >
        🙂
      </button>

      {pickerOpen && (
        <div className="emoji-popover" data-testid="emoji-popover">
          <EmojiPicker
            onEmojiClick={handleEmojiPick}
            theme={isDark ? Theme.DARK : Theme.LIGHT}
            searchPlaceholder="Search emoji"
            width={300}
            height={360}
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
          />
        </div>
      )}
    </div>
  );
}

export default ReactionBar;
